/**
 * Payment Analytics Hooks
 * 
 * Provides analytics and metrics for payment transactions across the platform.
 * These hooks enable administrators and finance teams to monitor payment health,
 * track success rates, validate payment methods, and identify issues.
 * 
 * **Available Hooks:**
 * 1. `usePaymentAnalyticsDaily` - Daily aggregated payment metrics
 * 2. `usePaymentSuccessByOrg` - Organization-level success rate analysis
 * 3. `usePaymentValidation` - Payment method validation utility
 * 
 * **Daily Analytics Metrics:**
 * - Transaction counts and volumes
 * - Fee calculations and payout totals
 * - Success/failure/refund breakdowns
 * - Time-series data for trend analysis
 * 
 * **Organization Success Metrics:**
 * - Per-organization transaction counts
 * - Success and failure rates
 * - Comparative performance analysis
 * 
 * **Use Cases:**
 * - Financial reporting and reconciliation
 * - Payment health monitoring
 * - Fraud detection and anomaly identification
 * - Performance optimization
 * 
 * @example
 * // Fetch 30 days of payment analytics
 * const { data: analytics } = usePaymentAnalyticsDaily(30);
 * 
 * analytics?.forEach(day => {
 *   console.log(`${day.date}: ${day.successful_payments} successes`);
 * });
 * 
 * @example
 * // Check organization payment success rates
 * const { data: orgMetrics } = usePaymentSuccessByOrg();
 * 
 * const lowPerformers = orgMetrics?.filter(org => 
 *   org.success_rate < 0.9
 * );
 * 
 * @example
 * // Validate payment method
 * const validate = usePaymentValidation();
 * const result = await validate(organizationId);
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';

export interface PaymentAnalyticsDaily {
  date: string;
  transaction_count: number;
  total_volume_cents: number;
  total_fees_cents: number;
  total_payouts_cents: number;
  successful_payments: number;
  failed_payments: number;
  refunded_payments: number;
}

export interface PaymentSuccessByOrg {
  organization_id: string;
  organization_name: string;
  total_transactions: number;
  successful_count: number;
  failed_count: number;
  success_rate: number;
}

export const usePaymentAnalyticsDaily = (days: number = 30) => {
  return useQuery({
    queryKey: ['payment-analytics-daily', days],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_payment_analytics_daily', { days_back: days });

      if (error) {
        console.error('Error fetching payment analytics:', error);
        throw error;
      }
      return (data || []) as PaymentAnalyticsDaily[];
    },
  });
};

export const usePaymentSuccessByOrg = () => {
  return useQuery({
    queryKey: ['payment-success-by-org'],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_payment_success_by_org');

      if (error) {
        console.error('Error fetching payment success by org:', error);
        throw error;
      }
      return (data || []) as PaymentSuccessByOrg[];
    },
  });
};

export interface PaymentValidationResult {
  valid: boolean;
  message?: string;
  reason?: string;
  paymentMethod?: {
    brand?: string;
    last4?: string;
    expMonth?: number;
    expYear?: number;
  };
  recentFailures?: Array<{ date: string; message: string }>;
}

export const usePaymentValidation = () => {
  return async (organizationId: string): Promise<PaymentValidationResult> => {
    const { data, error } = await invokeEdgeFunction('validate-payment-method', {
      body: { organizationId },
      callerOperation: 'usePaymentAnalytics.validateMethod',
    });

    if (error) throw error;
    return data as PaymentValidationResult;
  };
};
